/**
 * Trade-In Form Web Component
 * Handles photo uploads and form submission via App Proxy
 */

const APP_PROXY_URL = '/apps/trade-in';

/**
 * Decode HTML entities in a string
 * @param {string} str - String with HTML entities
 * @returns {string} - Decoded string
 */
function decodeHtmlEntities(str) {
  if (!str) return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
}

class TradeInForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.fileInput = null;
    this.dropZone = null;
    this.previewContainer = null;
    this.submitButton = null;
    this.statusBanner = null;
    this.selectedFiles = [];
    this.uploadedUrls = [];
    this.isSubmitting = false;

    // Bound event handlers for cleanup
    this.boundHandlers = {};
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.fileInput = this.querySelector('[data-file-input]');
    this.dropZone = this.querySelector('[data-drop-zone]');
    this.previewContainer = this.querySelector('[data-preview-container]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.statusBanner = this.querySelector('[data-status-banner]');
    this.brandSelect = this.querySelector('[name="brand"]');
    this.otherBrandField = this.querySelector('[data-other-brand-field]');
    this.conditionDescription = this.querySelector('[data-condition-description]');

    if (!this.form) return;

    this.bindEvents();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.form && this.boundHandlers.submit) {
      this.form.removeEventListener('submit', this.boundHandlers.submit);
    }

    if (this.fileInput && this.boundHandlers.fileChange) {
      this.fileInput.removeEventListener('change', this.boundHandlers.fileChange);
    }

    if (this.dropZone) {
      if (this.boundHandlers.dragOver) {
        this.dropZone.removeEventListener('dragover', this.boundHandlers.dragOver);
      }
      if (this.boundHandlers.dragLeave) {
        this.dropZone.removeEventListener('dragleave', this.boundHandlers.dragLeave);
      }
      if (this.boundHandlers.drop) {
        this.dropZone.removeEventListener('drop', this.boundHandlers.drop);
      }
      if (this.boundHandlers.click) {
        this.dropZone.removeEventListener('click', this.boundHandlers.click);
      }
      if (this.boundHandlers.keydown) {
        this.dropZone.removeEventListener('keydown', this.boundHandlers.keydown);
      }
    }

    // Clean up brand select listener
    if (this.brandSelect && this.boundHandlers.brandChange) {
      this.brandSelect.removeEventListener('change', this.boundHandlers.brandChange);
    }

    // Clean up condition grade listeners
    if (this.boundHandlers.conditionChange) {
      const conditionGrades = this.querySelectorAll('.trade-in-condition-grade');
      conditionGrades.forEach((grade) => {
        grade.removeEventListener('click', this.boundHandlers.conditionChange);
        const radio = grade.querySelector('input[type="radio"]');
        if (radio) {
          radio.removeEventListener('change', this.boundHandlers.conditionChange);
        }
      });
    }

    // Revoke any remaining object URLs
    this.revokeAllObjectUrls();
  }

  revokeAllObjectUrls() {
    if (this.previewContainer) {
      const images = this.previewContainer.querySelectorAll('img');
      images.forEach((img) => {
        if (img.src && img.src.startsWith('blob:')) {
          URL.revokeObjectURL(img.src);
        }
      });
    }
  }

  bindEvents() {
    // Form submission
    this.boundHandlers.submit = (e) => this.handleSubmit(e);
    this.form.addEventListener('submit', this.boundHandlers.submit);

    // File input change
    if (this.fileInput) {
      this.boundHandlers.fileChange = (e) => this.handleFileSelect(e);
      this.fileInput.addEventListener('change', this.boundHandlers.fileChange);
    }

    // Drag and drop
    if (this.dropZone) {
      this.boundHandlers.dragOver = (e) => this.handleDragOver(e);
      this.boundHandlers.dragLeave = (e) => this.handleDragLeave(e);
      this.boundHandlers.drop = (e) => this.handleDrop(e);
      this.boundHandlers.click = () => this.fileInput?.click();
      this.boundHandlers.keydown = (e) => this.handleDropZoneKeydown(e);

      this.dropZone.addEventListener('dragover', this.boundHandlers.dragOver);
      this.dropZone.addEventListener('dragleave', this.boundHandlers.dragLeave);
      this.dropZone.addEventListener('drop', this.boundHandlers.drop);
      this.dropZone.addEventListener('click', this.boundHandlers.click);
      this.dropZone.addEventListener('keydown', this.boundHandlers.keydown);
    }

    // Brand dropdown - show/hide "Other" field
    if (this.brandSelect) {
      this.boundHandlers.brandChange = (e) => this.handleBrandChange(e);
      this.brandSelect.addEventListener('change', this.boundHandlers.brandChange);
    }

    // Condition grade selection
    const conditionGrades = this.querySelectorAll('.trade-in-condition-grade');
    if (conditionGrades.length > 0) {
      this.boundHandlers.conditionChange = (e) => this.handleConditionChange(e);
      conditionGrades.forEach((grade) => {
        grade.addEventListener('click', this.boundHandlers.conditionChange);
        const radio = grade.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', this.boundHandlers.conditionChange);
        }
      });
    }
  }

  handleBrandChange(e) {
    const selectedValue = e.target.value;
    if (this.otherBrandField) {
      if (selectedValue === 'Other') {
        this.otherBrandField.style.display = 'block';
        const otherInput = this.otherBrandField.querySelector('input');
        if (otherInput) {
          otherInput.required = true;
        }
      } else {
        this.otherBrandField.style.display = 'none';
        const otherInput = this.otherBrandField.querySelector('input');
        if (otherInput) {
          otherInput.required = false;
          otherInput.value = '';
        }
      }
    }
  }

  handleConditionChange(e) {
    // Find the clicked grade element
    const gradeElement = e.target.closest('.trade-in-condition-grade');
    if (!gradeElement) return;

    const condition = gradeElement.dataset.condition;
    if (!condition) return;

    // Update visual selection
    const allGrades = this.querySelectorAll('.trade-in-condition-grade');
    allGrades.forEach((grade) => {
      grade.classList.remove('selected');
    });
    gradeElement.classList.add('selected');

    // Ensure the radio is checked
    const radio = gradeElement.querySelector('input[type="radio"]');
    if (radio) {
      radio.checked = true;
    }

    // Update description
    // Dataset converts data-desc-s to descS, data-desc-sa to descSa, etc.
    if (this.conditionDescription) {
      const descKey = `desc${condition.charAt(0)}${condition.slice(1).toLowerCase()}`;
      const description = this.conditionDescription.dataset[descKey] || '';
      this.conditionDescription.textContent = description;
    }
  }

  handleDropZoneKeydown(e) {
    // Open file picker on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.fileInput?.click();
    }
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropZone.classList.add('drag-over');
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropZone.classList.remove('drag-over');
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropZone.classList.remove('drag-over');

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );

    this.addFiles(files);
  }

  handleFileSelect(e) {
    const files = Array.from(e.target.files);
    this.addFiles(files);
  }

  addFiles(files) {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 25 * 1024 * 1024; // 25MB
    const maxFiles = 10;

    const validFiles = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        this.showError(`Invalid file type: ${file.name}. Please use JPEG, PNG, or WebP.`);
        return false;
      }
      if (file.size > maxSize) {
        this.showError(`File too large: ${file.name}. Maximum size is 25MB.`);
        return false;
      }
      return true;
    });

    // Add new files up to max
    const remainingSlots = maxFiles - this.selectedFiles.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (validFiles.length > remainingSlots) {
      this.showError(`Maximum ${maxFiles} photos allowed. Only ${remainingSlots} more can be added.`);
    }

    // Use immutable pattern
    this.selectedFiles = [...this.selectedFiles, ...filesToAdd];
    this.updatePreview();
    this.updateFileCount();
  }

  removeFile(index) {
    // Use immutable pattern instead of splice
    this.selectedFiles = [
      ...this.selectedFiles.slice(0, index),
      ...this.selectedFiles.slice(index + 1)
    ];
    this.updatePreview();
    this.updateFileCount();
  }

  updatePreview() {
    if (!this.previewContainer) return;

    // Revoke old object URLs before clearing
    this.revokeAllObjectUrls();
    this.previewContainer.innerHTML = '';

    this.selectedFiles.forEach((file, index) => {
      const preview = document.createElement('div');
      preview.className = 'trade-in-photo-preview';

      const img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      img.alt = `Photo ${index + 1}`;
      img.onload = () => URL.revokeObjectURL(img.src);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'trade-in-photo-remove';
      removeBtn.setAttribute('aria-label', `Remove photo ${index + 1}`);

      // Create SVG using DOM APIs (safe from XSS)
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '16');
      svg.setAttribute('height', '16');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('fill', 'currentColor');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z');

      svg.appendChild(path);
      removeBtn.appendChild(svg);
      removeBtn.addEventListener('click', () => this.removeFile(index));

      preview.appendChild(img);
      preview.appendChild(removeBtn);
      this.previewContainer.appendChild(preview);
    });
  }

  updateFileCount() {
    const countElement = this.querySelector('[data-file-count]');
    if (countElement) {
      const count = this.selectedFiles.length;
      countElement.textContent = count > 0
        ? `${count} photo${count !== 1 ? 's' : ''} selected`
        : '';
      countElement.classList.toggle('has-files', count > 0);
    }

    // Update validation state
    const minPhotos = 3;
    const errorElement = this.querySelector('[data-photos-error]');
    if (errorElement) {
      if (this.selectedFiles.length > 0 && this.selectedFiles.length < minPhotos) {
        errorElement.textContent = `Please upload at least ${minPhotos} photos (${minPhotos - this.selectedFiles.length} more needed)`;
        errorElement.style.display = 'block';
      } else {
        errorElement.style.display = 'none';
      }
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (this.isSubmitting) return;

    // Validate form
    if (!this.validateForm()) return;

    this.isSubmitting = true;
    this.setSubmitState(true);
    this.hideStatus();

    try {
      // Upload photos first
      this.uploadedUrls = await this.uploadPhotos();

      // Submit form data
      await this.submitFormData();

      // Show success
      this.showSuccess();
      this.resetForm();
    } catch (error) {
      this.showError(error.message || 'Something went wrong. Please try again.');
    } finally {
      this.isSubmitting = false;
      this.setSubmitState(false);
    }
  }

  validateForm() {
    const email = this.form.querySelector('[name="customer_email"]')?.value;
    const brand = this.form.querySelector('[name="brand"]')?.value;
    const brandOther = this.form.querySelector('[name="brand_other"]')?.value;
    const conditionGrade = this.form.querySelector('[name="condition_grade"]:checked')?.value;

    // Clear previous aria-invalid states
    this.form.querySelectorAll('[aria-invalid]').forEach((el) => {
      el.removeAttribute('aria-invalid');
    });

    // Clear previous selected state errors
    this.querySelectorAll('.trade-in-condition-grade').forEach((el) => {
      el.style.borderColor = '';
    });

    // Email validation (for guests)
    const emailInput = this.form.querySelector('[name="customer_email"]:not([type="hidden"])');
    if (emailInput) {
      if (!email) {
        this.showError('Email is required');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError('Please enter a valid email address');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return false;
      }
    }

    // Brand validation
    const brandInput = this.form.querySelector('[name="brand"]');
    if (!brand || brand.trim().length === 0) {
      this.showError('Please select a brand');
      if (brandInput) {
        brandInput.setAttribute('aria-invalid', 'true');
        brandInput.focus();
      }
      return false;
    }

    // Other brand validation (if "Other" is selected)
    if (brand === 'Other') {
      const otherBrandInput = this.form.querySelector('[name="brand_other"]');
      if (!brandOther || brandOther.trim().length === 0) {
        this.showError('Please enter the brand name');
        if (otherBrandInput) {
          otherBrandInput.setAttribute('aria-invalid', 'true');
          otherBrandInput.focus();
        }
        return false;
      }
    }

    // Condition grade validation
    if (!conditionGrade) {
      this.showError('Please select a condition grade');
      // Highlight the condition grades section
      const firstGrade = this.querySelector('.trade-in-condition-grade');
      if (firstGrade) {
        firstGrade.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    // Photos validation
    if (this.selectedFiles.length < 3) {
      this.showError('Please upload at least 3 photos');
      return false;
    }

    return true;
  }

  async uploadPhotos() {
    const uploadedUrls = [];
    const customerEmail = this.getCustomerEmail();
    const customerId = this.form.querySelector('[name="customer_id"]')?.value;

    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i];
      this.updateProgress(i + 1, this.selectedFiles.length);

      try {
        // Get signed URL
        const signedUrlParams = new URLSearchParams({
          filename: file.name,
          content_type: file.type,
          customer_email: customerEmail
        });

        if (customerId) {
          signedUrlParams.append('customer_id', customerId);
        }

        const signedUrlResponse = await fetch(
          `${APP_PROXY_URL}/signed-url?${signedUrlParams.toString()}`,
          { method: 'GET', credentials: 'same-origin' }
        );

        if (!signedUrlResponse.ok) {
          const errorData = await signedUrlResponse.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to get upload URL');
        }

        const { uploadUrl, publicUrl } = await signedUrlResponse.json();

        // Upload to GCS
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload photo');
        }

        uploadedUrls.push(publicUrl);
      } catch (error) {
        throw new Error(`Failed to upload photo ${i + 1}: ${error.message}`);
      }
    }

    return uploadedUrls;
  }

  async submitFormData() {
    const formData = new FormData(this.form);
    const customerId = formData.get('customer_id');

    // Get brand value - use "Other" brand name if selected
    let brandValue = formData.get('brand');
    if (brandValue === 'Other') {
      const otherBrand = formData.get('brand_other');
      if (otherBrand && otherBrand.trim()) {
        brandValue = otherBrand.trim();
      }
    }

    // Parse asking_price as a number, or null if empty
    const askingPriceValue = formData.get('asking_price');
    const askingPrice = askingPriceValue && askingPriceValue.trim() !== ''
      ? parseFloat(askingPriceValue)
      : null;

    const payload = {
      brand: brandValue,
      condition_grade: formData.get('condition_grade'),
      condition_notes: formData.get('condition_notes') || '',
      customer_email: this.getCustomerEmail(),
      customer_name: formData.get('customer_name') || '',
      photo_urls: this.uploadedUrls,
      additional_notes: formData.get('additional_notes') || '',
      asking_price: askingPrice
    };

    const submitParams = new URLSearchParams();
    if (customerId) {
      submitParams.append('logged_in_customer_id', customerId);
    }

    const submitUrl = `${APP_PROXY_URL}/submit${submitParams.toString() ? '?' + submitParams.toString() : ''}`;

    const response = await fetch(submitUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to submit form');
    }

    return response.json();
  }

  getCustomerEmail() {
    // Check for hidden customer email first (logged in user)
    const hiddenEmail = this.form.querySelector('[name="customer_email"][type="hidden"]');
    if (hiddenEmail) {
      return hiddenEmail.value;
    }
    // Otherwise get from visible input (guest)
    return this.form.querySelector('[name="customer_email"]')?.value || '';
  }

  setSubmitState(isSubmitting) {
    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
      const textElement = this.submitButton.querySelector('[data-submit-text]');
      const spinnerElement = this.submitButton.querySelector('[data-submit-spinner]');

      if (textElement) {
        textElement.textContent = isSubmitting
          ? this.submitButton.dataset.submittingText || 'Submitting...'
          : this.submitButton.dataset.submitText || 'Submit';
      }

      if (spinnerElement) {
        spinnerElement.style.display = isSubmitting ? 'inline-block' : 'none';
      }
    }
  }

  updateProgress(current, total) {
    const progressElement = this.querySelector('[data-upload-progress]');
    if (progressElement) {
      progressElement.textContent = `Uploading photo ${current} of ${total}...`;
      progressElement.style.display = 'block';
    }
  }

  showSuccess() {
    if (!this.statusBanner) return;

    this.statusBanner.className = 'banner banner--success';

    // Clear previous content
    this.statusBanner.innerHTML = '';

    // Build DOM safely (XSS-safe)
    const content = document.createElement('div');
    content.className = 'trade-in-status-content';

    // Success icon (SVG created via DOM APIs)
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'trade-in-status-icon');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');

    const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('points', '20 6 9 17 4 12');
    svg.appendChild(polyline);

    // Text content
    const textDiv = document.createElement('div');

    const strong = document.createElement('strong');
    // Decode HTML entities from Liquid escape filter
    strong.textContent = decodeHtmlEntities(this.dataset.successTitle) || 'Request Submitted!';

    const p = document.createElement('p');
    // Decode HTML entities from Liquid escape filter
    p.textContent = decodeHtmlEntities(this.dataset.successMessage) || 'Thank you! We will review your submission and get back to you within 24-48 hours.';

    textDiv.appendChild(strong);
    textDiv.appendChild(p);

    content.appendChild(svg);
    content.appendChild(textDiv);
    this.statusBanner.appendChild(content);

    this.statusBanner.style.display = 'block';
    this.statusBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Hide progress
    const progressElement = this.querySelector('[data-upload-progress]');
    if (progressElement) {
      progressElement.style.display = 'none';
    }
  }

  showError(message) {
    if (!this.statusBanner) return;

    this.statusBanner.className = 'banner banner--error';

    // Clear previous content
    this.statusBanner.innerHTML = '';

    // Build DOM safely (XSS-safe)
    const content = document.createElement('div');
    content.className = 'trade-in-status-content';

    // Error icon (SVG created via DOM APIs)
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'trade-in-status-icon');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');

    const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '15');
    line1.setAttribute('y1', '9');
    line1.setAttribute('x2', '9');
    line1.setAttribute('y2', '15');

    const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '9');
    line2.setAttribute('y1', '9');
    line2.setAttribute('x2', '15');
    line2.setAttribute('y2', '15');

    svg.appendChild(circle);
    svg.appendChild(line1);
    svg.appendChild(line2);

    // Text content
    const textDiv = document.createElement('div');

    const strong = document.createElement('strong');
    // Decode HTML entities from Liquid escape filter
    strong.textContent = decodeHtmlEntities(this.dataset.errorTitle) || 'Error';

    const p = document.createElement('p');
    p.textContent = message; // Safe - textContent auto-escapes

    textDiv.appendChild(strong);
    textDiv.appendChild(p);

    content.appendChild(svg);
    content.appendChild(textDiv);
    this.statusBanner.appendChild(content);

    this.statusBanner.style.display = 'block';
    this.statusBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Hide progress
    const progressElement = this.querySelector('[data-upload-progress]');
    if (progressElement) {
      progressElement.style.display = 'none';
    }
  }

  hideStatus() {
    if (this.statusBanner) {
      this.statusBanner.style.display = 'none';
    }
  }

  resetForm() {
    this.form.reset();
    this.selectedFiles = [];
    this.uploadedUrls = [];
    this.updatePreview();
    this.updateFileCount();

    // Reset condition grade selection
    const conditionGrades = this.querySelectorAll('.trade-in-condition-grade');
    conditionGrades.forEach((grade) => {
      grade.classList.remove('selected');
    });

    // Reset condition description
    if (this.conditionDescription) {
      this.conditionDescription.textContent = '';
    }

    // Hide other brand field
    if (this.otherBrandField) {
      this.otherBrandField.style.display = 'none';
    }

    // Hide form and show only success message
    const formContent = this.querySelector('[data-form-content]');
    if (formContent) {
      formContent.style.display = 'none';
    }
  }
}

// Register the custom element
if (!customElements.get('trade-in-form')) {
  customElements.define('trade-in-form', TradeInForm);
}
